import {
  Button,
  ButtonGroup,
  formatDate,
  Hero,
  Page,
  formatCurrency,
  Spinner,
  useAccessTokenManagement,
  ErrorSummary
} from '@hbo/ui-servicing-react-components';
import classNames from 'classnames';
import CollapsibleCard from 'components/CollapsibleCard';
import Column from 'components/Column';
import Columns from 'components/Columns';
import { DECLINED } from 'constants/constants';
import { useCase } from 'hooks/useCase';
import AssessmentModal from 'pages/Case/components/AssessmentModal';
import CancelledDeclineModal from 'pages/Case/components/CancelledDeclineModal';
import CompletedModal from 'pages/Case/components/CompletedModal';
import MilestoneButton from 'pages/Case/components/MilestoneButton';
import OfferModal from 'pages/Case/components/OfferModal';
import ValuationModal from 'pages/Case/components/ValuationModal';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthenticationService } from 'services/authentication';
import { CaseTrackingService } from 'services/case';
import { RoutePath } from 'types/global';
import { formatAddress } from 'utils';

const CaseTracking: React.FC = () => {
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useCase();
  const { isAuthenticating, isRevokingToken } = useAccessTokenManagement();

  const [isValuationModalVisible, setIsValidationModalVisible] = useState<boolean>(false);
  const [isAssessmentModalVisible, setIsAssessmentModalVisible] = useState<boolean>(false);
  const [isOfferModalVisible, setIsOfferModalVisible] = useState<boolean>(false);
  const [isCompletedModalVisible, setIsCompletedModalVisible] = useState<boolean>(false);
  const [isCancelledDeclineModalVisible, setIsCancelledDeclineModalVisible] =
    useState<boolean>(false);

  if (isLoading || isAuthenticating || isRevokingToken) {
    return <Spinner message="Loading" isFullscreen />;
  }

  const applicants = (data?.applicants || []).sort(
    (a, b) => Number(b.isMainApplicant) - Number(a.isMainApplicant)
  );

  const applicantData = [
    ['Full name', applicants.map((applicant) => applicant?.name)],
  ];

  const propertyData = [
    ['Property To Be Mortgaged', formatAddress(data?.propertyAddress, false)],
    ['Property To Be Mortgaged Postcode', data?.propertyAddress?.postcode]
  ]
  
  const statusClasses = classNames({
    'text-red': data?.mortgageSummary?.applicationStatus.includes(DECLINED)
  });

  return (
    <>
      <Helmet>
        <title>{t('case.title')}</title>
      </Helmet>
      <Hero>
        <Hero.PrimaryContent title={<Trans i18nKey="case.title" />} />
      </Hero>
      <Page>
        <Page.Content>
          {isError ? (
            <ErrorSummary title="">
              <span className="sr-only">{t('generic.screenReaderError')}</span>
              {error.code.response.data.errorCode === CaseTrackingService.CustomStatusCode.Error
                ? error.code.response.data.errorMessages[0]
                : error.message}
            </ErrorSummary>
          ) : (
            <>
              <div className="cards-container">
                <CollapsibleCard
                  id="case-mortgage-summary"
                  title={t('case.mortgageSummary.title')}
                  secondaryTitle={`${t('case.milestones')}`}
                >
                  <Columns>
                    <Column mobile={6} tablet={6} desktop={6} widescreen={6}>
                      <table className="case-details">
                        <tbody>
                          {[
                            [
                              'Application Status',
                              <div className={statusClasses}>
                                {data?.mortgageSummary?.applicationStatus}
                              </div>
                            ],
                            [
                              'Latest Offer Date',
                              formatDate(data?.mortgageSummary?.latestOfferDate)
                            ],
                            [
                              'Expected Completion Date',
                              formatDate(data?.mortgageSummary?.expectedCompletionDate)
                            ]
                          ].map(([label, value], index) => (
                            <tr key={`summary-${index}`}>
                              <th>{label}</th>
                              <td>{value || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Column>
                    <Column mobile={6} tablet={6} desktop={6} widescreen={6}>
                      <table className="case-details">
                        <tbody>
                          {data?.mortgageSummary?.milestones?.valuation ? (
                            <MilestoneButton
                              title="Valuation"
                              isComplete={data!.mortgageSummary.milestones.valuation.isComplete}
                              status={data!.mortgageSummary.milestones.valuation.currentStatus}
                              onClick={() => setIsValidationModalVisible(true)}
                            />
                          ) : null}

                          {data?.mortgageSummary?.milestones?.assessment ? (
                            <MilestoneButton
                              title="Assessment"
                              isComplete={data!.mortgageSummary.milestones.assessment.isComplete}
                              status={data!.mortgageSummary.milestones.assessment.currentStatus}
                              onClick={() => setIsAssessmentModalVisible(true)}
                            />
                          ) : null}

                          {data?.mortgageSummary?.milestones?.offer ? (
                            <MilestoneButton
                              title="Offer"
                              isComplete={data!.mortgageSummary.milestones.offer.isComplete}
                              status={data!.mortgageSummary.milestones.offer.currentStatus}
                              onClick={() => setIsOfferModalVisible(true)}
                            />
                          ) : null}

                          {data?.mortgageSummary?.milestones?.completion ? (
                            <MilestoneButton
                              title="Completion"
                              isComplete={data!.mortgageSummary.milestones.completion.isComplete}
                              status={data!.mortgageSummary.milestones.completion.currentStatus}
                              onClick={() => setIsCompletedModalVisible(true)}
                            />
                          ) : null}

                          {data?.mortgageSummary?.milestones?.cancelledDecline ? (
                            <MilestoneButton
                              title={
                                data!.mortgageSummary.milestones.cancelledDecline.currentStatus
                              }
                              isComplete={
                                data!.mortgageSummary.milestones.cancelledDecline.isComplete
                              }
                              status={
                                data!.mortgageSummary.milestones.cancelledDecline.currentStatus
                              }
                              onClick={() => setIsCancelledDeclineModalVisible(true)}
                            />
                          ) : null}
                        </tbody>
                      </table>
                    </Column>
                  </Columns>
                </CollapsibleCard>
                <CollapsibleCard
                  id="case-applicant-details"
                  title={t('case.applicantDetails.title')}
                  isClosed
                >
                  <table className="case-details">
                    <tbody>
                      {applicantData?.map(([label, applicants], index) => (
                        <tr key={`applicant-details-${index}`}>
                          <th>{label}</th>
                          <td>{applicants.length ? applicants[0] : '-'}</td>
                          {data?.applicants?.length === 2 && <td>{applicants[1] || '-'}</td>}
                        </tr>
                      ))}
                      {propertyData?.map(([label, properties], index) => (
                        <tr key={`property-details-${index}`}>
                          <th>{label}</th>
                          <td>{properties || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CollapsibleCard>

                <CollapsibleCard
                  id="case-product-overview"
                  title={t('case.productOverview.title')}
                  isClosed
                >
                  <table className="case-details">
                    <tbody>
                      {[
                        ['Loan Amount', formatCurrency(data?.productInfo?.totalLoanAmount)],
                        ['LTV', data?.productInfo?.ltv && `${data?.productInfo?.ltv}%`],
                        ['Application Type', data?.productInfo?.applicationType],
                        ['Product Fee Amount', formatCurrency(data?.productInfo?.feeAmount)],
                        // ['Product Fee Status', data?.productInfo?.feeStatus],
                        ['Cashback Amount', formatCurrency(data?.productInfo?.cashback)],
                        [
                          'Monthly Repayment Amount',
                          formatCurrency(data?.productInfo?.monthlyRepaymentAmount)
                        ]
                      ].map(([label, value], index) => (
                        <tr key={`product-overview-${index}`}>
                          <th>{label}</th>
                          <td>{value || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CollapsibleCard>

                <CollapsibleCard
                  id="case-product-details"
                  title={t('case.productDetails.title')}
                  isClosed
                >
                  {data?.productInfo?.products?.map((product, productIndex) => (
                    <table className="case-details" key={`product-${productIndex}`}>
                      <tbody>
                        {[
                          ['Product', product?.desc],
                          ['Product Description', product?.altDesc],
                          [
                            'Term Duration',
                            `${product?.termYears} years, ${product?.termMonths} months`
                          ]
                        ].map(([label, value], index) => (
                          <tr key={`product-${index}`}>
                            <th>{label}</th>
                            <td>{value || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ))}
                </CollapsibleCard>

                <CollapsibleCard
                  id="case-solicitor-details"
                  title={t('case.solicitorDetails.title')}
                  isClosed
                >
                  <table className="case-details">
                    <tbody>
                      {[
                        ['Solicitor Firm Name', data?.solicitorInformation?.firmName],
                        ['Solicitor Contact Name', data?.solicitorInformation?.contactName],
                        ['Telephone Number', data?.solicitorInformation?.contact?.phone],
                        ['Email', data?.solicitorInformation?.contact?.email]
                      ].map(([label, value], index) => (
                        <tr key={`solicitor-details-${index}`}>
                          <th>{label}</th>
                          <td>{value || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CollapsibleCard>
              </div>
            </>
          )}
        </Page.Content>
        {isValuationModalVisible && (
          <ValuationModal onClose={() => setIsValidationModalVisible(false)} />
        )}

        {isAssessmentModalVisible && (
          <AssessmentModal onClose={() => setIsAssessmentModalVisible(false)} />
        )}

        {isOfferModalVisible && <OfferModal onClose={() => setIsOfferModalVisible(false)} />}

        {isCompletedModalVisible && (
          <CompletedModal onClose={() => setIsCompletedModalVisible(false)} />
        )}

        {isCancelledDeclineModalVisible && (
          <CancelledDeclineModal onClose={() => setIsCancelledDeclineModalVisible(false)} />
        )}
      </Page>
    </>
  );
};

export default CaseTracking;
